import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, MessageSquare, Phone, Building2, Loader2, Trash2, Save, Check } from 'lucide-react';

interface TeamMember {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

interface RepCommStatus {
  rep_id: string;
  email_provider: 'gmail' | 'outlook' | null;
  email_address: string | null;
  phone_e164: string | null;
  forward_to_e164: string | null;
  forward_enabled: boolean;
}

interface TeamsConfig {
  dealership_id: string;
  teams_connection_id: string | null;
  team_id: string | null;
  channel_id_new_lead: string | null;
  channel_id_alerts: string | null;
  channel_id_digest: string | null;
  digest_time_local: string;
  digest_timezone: string;
  enabled: boolean;
}

export function TeamCommunicationsCard() {
  const { role, mode, dealershipId, isSolo } = useAuth();
  const isAdmin = role === 'manager' || role === 'admin';
  const isReal = mode === 'real';

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [commStatus, setCommStatus] = useState<RepCommStatus[]>([]);
  const [teamsCfg, setTeamsCfg] = useState<TeamsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingPhone, setSavingPhone] = useState<Record<string, boolean>>({});
  const [savingTeams, setSavingTeams] = useState(false);
  const [phoneInput, setPhoneInput] = useState<Record<string, string>>({});
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<{ repId: string; phone: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Teams form state
  const [teamsForm, setTeamsForm] = useState<Partial<TeamsConfig>>({});

  useEffect(() => {
    if (!isAdmin || !isReal || !dealershipId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      // Load team members
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email, full_name, dealership_id')
        .eq('dealership_id', dealershipId);
      const ids = (profiles ?? []).map((p) => p.id);
      const { data: roles } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', ids.length ? ids : ['00000000-0000-0000-0000-000000000000']);
      const roleMap = new Map((roles ?? []).map((r) => [r.user_id, r.role as string]));
      const merged: TeamMember[] = (profiles ?? []).map((p) => ({
        id: p.id,
        email: p.email,
        full_name: p.full_name,
        role: roleMap.get(p.id) ?? 'rep',
      }));

      // Load communication status
      const { data: emails } = await supabase
        .from('rep_email_accounts')
        .select('rep_id, provider, email_address, status')
        .in('rep_id', ids)
        .eq('status', 'active');
      const { data: phones } = await supabase
        .from('rep_phone_numbers')
        .select('rep_id, phone_e164, forward_to_e164, forward_enabled, status')
        .in('rep_id', ids)
        .eq('status', 'active');

      const emailMap = new Map((emails ?? []).map((e) => [e.rep_id, e]));
      const phoneMap = new Map((phones ?? []).map((p) => [p.rep_id, p]));

      const status: RepCommStatus[] = merged.map((m) => {
        const e = emailMap.get(m.id);
        const p = phoneMap.get(m.id);
        return {
          rep_id: m.id,
          email_provider: (e?.provider as 'gmail' | 'outlook') ?? null,
          email_address: e?.email_address ?? null,
          phone_e164: p?.phone_e164 ?? null,
          forward_to_e164: p?.forward_to_e164 ?? null,
          forward_enabled: p?.forward_enabled ?? false,
        };
      });

      // Load Teams config
      const { data: cfg } = await supabase
        .from('dealership_teams_config')
        .select('*')
        .eq('dealership_id', dealershipId)
        .maybeSingle();

      if (!active) return;
      setMembers(merged);
      setCommStatus(status);
      if (cfg) {
        setTeamsCfg(cfg as TeamsConfig);
        setTeamsForm(cfg as TeamsConfig);
      } else {
        setTeamsCfg(null);
        setTeamsForm({
          digest_time_local: '08:00:00',
          digest_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          enabled: false,
        });
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [isAdmin, isReal, dealershipId, refreshKey]);

  const getStatus = (repId: string) => commStatus.find((c) => c.rep_id === repId);

  const assignPhone = async (repId: string) => {
    const raw = phoneInput[repId]?.trim();
    if (!raw) return;
    // Normalize to E.164-ish format for storage (basic validation)
    const normalized = raw.startsWith('+') ? raw : `+${raw.replace(/[^0-9]/g, '')}`;
    if (normalized.length < 10) {
      toast.error('Enter a valid phone number with country code (e.g. +15195551234)');
      return;
    }
    setSavingPhone((s) => ({ ...s, [repId]: true }));
    const { error } = await supabase.from('rep_phone_numbers').upsert({
      rep_id: repId,
      dealership_id: dealershipId!,
      phone_e164: normalized,
      status: 'active',
      forward_enabled: true,
    }, { onConflict: 'rep_id' });
    setSavingPhone((s) => ({ ...s, [repId]: false }));
    if (error) {
      toast.error('Could not assign number: ' + error.message);
      return;
    }
    toast.success('Number assigned');
    setPhoneInput((p) => ({ ...p, [repId]: '' }));
    setRefreshKey((k) => k + 1);
  };

  const releasePhone = async () => {
    if (!releaseTarget) return;
    setSavingPhone((s) => ({ ...s, [releaseTarget.repId]: true }));
    const { error } = await supabase
      .from('rep_phone_numbers')
      .delete()
      .eq('rep_id', releaseTarget.repId);
    setSavingPhone((s) => ({ ...s, [releaseTarget.repId]: false }));
    setReleaseDialogOpen(false);
    setReleaseTarget(null);
    if (error) {
      toast.error('Could not release number: ' + error.message);
      return;
    }
    toast.success('Number released');
    setRefreshKey((k) => k + 1);
  };

  const saveTeamsConfig = async () => {
    if (!dealershipId) return;
    setSavingTeams(true);
    const payload = {
      dealership_id: dealershipId,
      teams_connection_id: teamsForm.teams_connection_id || null,
      team_id: teamsForm.team_id || null,
      channel_id_new_lead: teamsForm.channel_id_new_lead || null,
      channel_id_alerts: teamsForm.channel_id_alerts || null,
      channel_id_digest: teamsForm.channel_id_digest || null,
      digest_time_local: teamsForm.digest_time_local || '08:00:00',
      digest_timezone: teamsForm.digest_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      enabled: teamsForm.enabled ?? false,
    };
    const { error } = await supabase
      .from('dealership_teams_config')
      .upsert(payload, { onConflict: 'dealership_id' });
    setSavingTeams(false);
    if (error) {
      toast.error('Could not save Teams config: ' + error.message);
      return;
    }
    toast.success('Teams configuration saved');
    setTeamsCfg(payload as TeamsConfig);
  };

  if (!isAdmin || !isReal || isSolo) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" /> Team Communications
          </CardTitle>
          <CardDescription>
            Assign texting numbers to reps and view their email connections.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No team members yet.</p>
          ) : (
            <div className="space-y-3">
              {members.map((m) => {
                const status = getStatus(m.id);
                const hasEmail = !!status?.email_provider;
                const hasPhone = !!status?.phone_e164;
                const inputVal = phoneInput[m.id] ?? '';
                const saving = savingPhone[m.id];
                return (
                  <div
                    key={m.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border border-border"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{m.full_name || m.email}</p>
                        <Badge variant="secondary" className="capitalize text-[10px]">
                          {m.role === 'rep' ? 'Rep' : m.role}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        {hasEmail ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                            <Check className="h-3 w-3" />
                            <Mail className="h-3 w-3" />
                            {status?.email_provider === 'gmail' ? 'Gmail' : 'Outlook'} connected
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> No email connected
                          </span>
                        )}
                        {hasPhone ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                            <Check className="h-3 w-3" />
                            <MessageSquare className="h-3 w-3" />
                            {status?.phone_e164}
                            {status?.forward_to_e164 && (
                              <span className="text-muted-foreground">→ {status.forward_to_e164}</span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <MessageSquare className="h-3 w-3" /> No number assigned
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0">
                      {hasPhone ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-400 hover:text-red-300"
                          disabled={saving}
                          onClick={() => {
                            setReleaseTarget({ repId: m.id, phone: status!.phone_e164! });
                            setReleaseDialogOpen(true);
                          }}
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          <span className="ml-1.5">Release</span>
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="+15195551234"
                            value={inputVal}
                            onChange={(e) => setPhoneInput((p) => ({ ...p, [m.id]: e.target.value }))}
                            className="h-8 w-40 text-sm"
                          />
                          <Button
                            size="sm"
                            onClick={() => assignPhone(m.id)}
                            disabled={saving || !inputVal.trim()}
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Microsoft Teams config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" /> Microsoft Teams Alerts
          </CardTitle>
          <CardDescription>
            Post alerts, new leads, and daily digests to your dealership's Teams channels.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="teams-enabled">Enable Teams notifications</Label>
              <p className="text-xs text-muted-foreground">
                When on, PullOver will post to your mapped channels.
              </p>
            </div>
            <Switch
              id="teams-enabled"
              checked={teamsForm.enabled ?? false}
              onCheckedChange={(v) => setTeamsForm((f) => ({ ...f, enabled: v }))}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="team-id">Teams Team ID</Label>
              <Input
                id="team-id"
                value={teamsForm.team_id ?? ''}
                onChange={(e) => setTeamsForm((f) => ({ ...f, team_id: e.target.value }))}
                placeholder="paste team GUID"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="connection-id">Connection ID (optional)</Label>
              <Input
                id="connection-id"
                value={teamsForm.teams_connection_id ?? ''}
                onChange={(e) => setTeamsForm((f) => ({ ...f, teams_connection_id: e.target.value }))}
                placeholder="connector reference"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ch-new-lead">New Lead channel ID</Label>
              <Input
                id="ch-new-lead"
                value={teamsForm.channel_id_new_lead ?? ''}
                onChange={(e) => setTeamsForm((f) => ({ ...f, channel_id_new_lead: e.target.value }))}
                placeholder="channel GUID"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ch-alerts">Alerts channel ID</Label>
              <Input
                id="ch-alerts"
                value={teamsForm.channel_id_alerts ?? ''}
                onChange={(e) => setTeamsForm((f) => ({ ...f, channel_id_alerts: e.target.value }))}
                placeholder="channel GUID"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ch-digest">Digest channel ID</Label>
              <Input
                id="ch-digest"
                value={teamsForm.channel_id_digest ?? ''}
                onChange={(e) => setTeamsForm((f) => ({ ...f, channel_id_digest: e.target.value }))}
                placeholder="channel GUID"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="digest-time">Digest send time</Label>
              <Input
                id="digest-time"
                type="time"
                value={(teamsForm.digest_time_local ?? '08:00:00').slice(0, 5)}
                onChange={(e) => {
                  const t = e.target.value;
                  setTeamsForm((f) => ({ ...f, digest_time_local: t ? `${t}:00` : '08:00:00' }));
                }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="digest-tz">Timezone</Label>
            <Select
              value={teamsForm.digest_timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
              onValueChange={(v) => setTeamsForm((f) => ({ ...f, digest_timezone: v }))}
            >
              <SelectTrigger id="digest-tz">
                <SelectValue />
              </SelectTrigger>
            <SelectContent className="max-h-60">
                {[
                  'America/Toronto', 'America/New_York', 'America/Chicago',
                  'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
                  'America/Anchorage', 'America/Halifax', 'America/Winnipeg',
                  'America/Edmonton', 'America/Vancouver', 'Europe/London',
                  'Europe/Paris', 'Europe/Berlin', 'Australia/Sydney',
                  'Asia/Dubai', 'Asia/Tokyo', 'Pacific/Auckland',
                ].map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveTeamsConfig} disabled={savingTeams}>
              {savingTeams ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save Teams config
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Release confirmation dialog */}
      <Dialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release phone number?</DialogTitle>
            <DialogDescription>
              This will unassign {releaseTarget?.phone} from the rep. They won't be able to send texts until reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={releasePhone}>
              Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
